
import { mapGetters, mapState } from 'vuex'

import * as endpoints from '~/api/endpoints'

import { ifHasValue } from '~/utils/optional-values'
import {
  getProductDescription,
  getProductIdFromSlug,
  getProductName,
} from '~/utils/product-info'
import { types as itemTypes } from '~/utils/product-item-type'
import { createMetaTags } from '~/utils/seo'

import BuyProduct from '~/components/create-order/buy/buy-product.vue'
import LoginDialog from '~/components/login-dialog/login-dialog.vue'

export default {
  components: {
    BuyProduct,
    LoginDialog,
  },

  layout: 'default',

  meta: {
    isPublic: true,
  },

  data() {
    return {
      stats: {
        countSellOrderBottles: null,
        avgSellOrderPrice: null,
        minSellOrderPrice: null,
        maxSellOrderPrice: null,
        lastTradeDate: null,
        lastTradePrice: null,
        availabilityOnWwi: null,
        projectedPriceOnBottling: null,
        swexMarketPrice: null,
      },
      details: null,
      imageFileNames: null,
      datoImages: null,
      producerLogo: null,
      producerCaskLogo: null,

      priceDevelopment: [],
      swexOtcs: [],
      projectedDevelopment: [],
      swexTrades: [],

      itemType: itemTypes.Bottle,
      orders: null,
      marketAvailability: {
        quantityAvailableOnMarket: 0,
        priceFrom: null,
      },
      isLoginDialogOpen: false,
    }
  },

  async fetch() {
    await this.loadDetails()

    await Promise.all([
      this.loadChart(),
      this.loadOrders(),
      this.loadMarketAvailability(),
      ...(this.isLoggedIn ? [this.loadPortfolioItem()] : []),
    ])
  },

  async mounted() {
    await this.loadStats()
  },

  head() {
    return {
      title: this.productDetails,
      meta: [...this.meta],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `https://theswex/${this.productLink}`,
        },
      ],
    }
  },

  computed: {
    ...mapState('auth', ['isLoggedIn']),

    ...mapGetters('trader', ['permissions']),

    productId() {
      return getProductIdFromSlug(this.$route.params.slug)
    },
    productName() {
      const detailsWithItemType = {
        ...this.details,
        itemType: this.itemType,
      }

      return getProductName(detailsWithItemType, {
        includeBatchName: false,
      })
    },
    productDetails() {
      const detailsWithItemType = {
        ...this.details,
        itemType: this.itemType,
      }

      return getProductDescription(detailsWithItemType)
    },
    productLink() {
      return this.$nuxt.context.app.localePath(
        `/whisky/${this.$route.params.slug}`,
      )
    },
    facts() {
      const {
        region,
        bottleryName,
        bottlerName,
        batchName,
        collection,
        yearBottled,
        yearDistillation,
        distillationDate,
        originalContent,
        maturationAge,
        remainingYearsTillMaturation,
        expectedBottlingYear,
        bottlesAvailableForSale,
        bottlesOnMaturation,
        volumeInMl,
        alcoholPercentage,
        distilleryName,
        distilleryRating,
        caskNumber,
        totalBottleCountOnLabel,
        age,
        currentAge,
        caskType,
        caskVolumeLiter,
        wbRating,
        wbVotes,
        itemType,
      } = this.details

      return [
        {
          name: this.$gettext('Cask number'),
          value: caskNumber,
        },
        {
          name: this.$gettext('Distiller'),
          value: distilleryName,
          filter: 'DistilleryName',
        },
        {
          name: this.$gettext('Bottler'),
          value: bottleryName,
          filter: 'BottleryName',
        },
        {
          name: this.$gettext('Independant Bottler'),
          value: bottlerName,
          filter: 'BottlerName',
        },
        {
          name: this.$gettext('Cask type and volume'),
          value: caskType ? `${caskType} ${caskVolumeLiter} liter` : null,
        },
        {
          name: this.$gettext('Original content'),
          value: originalContent,
        },
        {
          name: this.$gettext('Collection'),
          value: batchName,
        },
        {
          name: this.$gettext('Collection'),
          value: collection,
          filter: 'Collection',
        },
        { name: this.$gettext('Region'), value: region, filter: 'Region' },
        {
          name: this.$gettext('Distillation year'),
          value: yearDistillation,
          filter: 'YearDistillation',
          hasRange: true,
        },
        {
          name: this.$gettext('Distillation date'),
          value: this.$formatAsDate(distillationDate),
        },
        {
          name: this.$gettext('Targeted bottling age (TBA)'),
          value: maturationAge,
        },
        {
          name: this.$gettext('Current age'),
          value: currentAge,
        },
        {
          name: this.$gettext('Remaining years until bottling'),
          value: this.$isCask({ itemType })
            ? this.$gettext(
                '%d until %s',
                remainingYearsTillMaturation,
                expectedBottlingYear,
              )
            : null,
        },
        {
          name: this.$gettext('# on TheSWEX'),
          value: this.$isBottle({ itemType }) ? bottlesAvailableForSale : null,
        },
        {
          name: this.$gettext('Expected # bottles on TBA'),
          value: bottlesOnMaturation,
        },
        {
          name: this.$gettext('Bottle year'),
          value: yearBottled,
          filter: 'YearBottling',
          hasRange: true,
        },
        {
          name: this.$gettext('Age'),
          value: ifHasValue(age, (value) =>
            this.$ngettext('%d year', '%d years', value, value),
          ),
          filter: 'Age',
          hasRange: true,
          rawValue: age,
        },
        {
          name: this.$gettext('Amount of bottles'),
          value: this.$formatAsNumber(totalBottleCountOnLabel),
        },
        {
          name: this.$gettext('Alcohol'),
          value: this.$formatAsPercentage(alcoholPercentage),
        },
        {
          name: this.$gettext('Volume'),
          value: this.$isCask({ itemType })
            ? null
            : this.$formatAsAmount(volumeInMl),
        },
        {
          name: this.$gettext('Whiskybase rating'),
          value: ifHasValue(
            wbRating,
            () => `${wbRating}/100 (${this.$formatAsNumber(wbVotes)} votes)`,
          ),
        },
        {
          name: this.$gettext('Distillery rating'),
          value: this.$formatAsNumber(distilleryRating),
        },
      ]
    },

    meta() {
      const metaData = {
        type: 'website',
        title: this.productDetails,
        description: this.productDetails,
        url: `https://theswex/${this.productLink}`,
        mainImage: this.datoImages && this.datoImages.FRONT,
      }
      metaData.details = this.details
      return createMetaTags(metaData)
    },
  },

  methods: {
    async loadStats() {
      this.stats = await this.$axios.$get(
        endpoints.getProductDetailsStats(this.productId),
      )
    },

    async loadChart() {
      const chart = await this.loadChartData(this.productId)

      this.priceDevelopment = chart?.swexMarketPrice?.points ?? []
      this.swexOtcs = chart?.swexOtcs?.points ?? []
      this.projectedDevelopment = chart?.projectedDevelopment?.points ?? []
      this.swexTrades = chart?.swexTrades?.points ?? []

      this.historicalDevelopment = chart?.historicalDevelopment?.points ?? []
      this.projectedDevelopment = chart?.projectedDevelopment?.points ?? []
    },

    async loadOrders() {
      // Maxcount is added to be able to show all the orders in the orderbook
      const orders = await this.$axios
        .$get(endpoints.getProductOrders(this.productId) + '?maxCount=99999')
        .catch(() => null)
      this.orders = orders
    },

    async loadDetails() {
      const result = await this.$axios.$get(
        endpoints.getProductDetails(this.productId),
      )

      this.itemType = result.itemType

      if (this.$isBottle({ itemType: this.itemType })) {
        this.details = result.bottleModel
        this.producerLogo = result.bottleModel.distilleryLogoImageUrl
      } else if (this.$isCask({ itemType: this.itemType })) {
        this.details = result.caskModel
        this.producerLogo = result.caskModel.distilleryLogoImageUrl
        this.producerCaskLogo = result.caskModel.caskImageUrl
      }
    },

    async loadPortfolioItem() {
      const portfolioItem = await this.$axios
        .$get(endpoints.getPortfolioByItemId(this.productId))
        .catch(() => null)
      this.portfolioItem = portfolioItem
    },

    async loadMarketAvailability() {
      const defaultBuyOrderData = await this.$axios
        .$get(endpoints.getBuyOrderDefaults, {
          params: {
            itemId: this.details.itemId,
          },
        })
        .catch(() => null)

      if (defaultBuyOrderData.marketAvailability) {
        this.marketAvailability = defaultBuyOrderData.marketAvailability
      }
    },

    loadChartData() {
      return this.$axios
        .$get(endpoints.getProductDetailsStockChartMarketPrice(this.productId))
        .catch(() => null)
    },

    openLoginDialog() {
      this.isLoginDialogOpen = true
    },

    closeLoginDialog() {
      this.isLoginDialogOpen = false
    },
  },
}
